import { UntitledIcon } from '@faceup/icons'
import { ulAlertTriangle } from '@faceup/icons/ulAlertTriangle'
import { ulSearchMd } from '@faceup/icons/ulSearchMd'
import { ulTrash01 } from '@faceup/icons/ulTrash01'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { trpc, useMutation, useSuspenseQuery } from '@faceup/trpc'
import { withModalSuspense } from '@faceup/ui'
import { Alert, AutoComplete, Button, Input, Modal, Table, Typography } from '@faceup/ui-base'
import { useState } from 'react'
import { useMotherId } from '../../providers'

const messages = defineMessages('administration.lastInvestigatorModal', [
  { id: 'titleDelete', defaultMessage: 'Delete user' },
  { id: 'titleRevoke', defaultMessage: 'Edit permissions' },
  {
    id: 'alertTitleDelete',
    defaultMessage: 'Assign a new investigator before deleting',
  },
  {
    id: 'alertTitleRevoke',
    defaultMessage: 'Assign a new investigator before changing permissions',
  },
  {
    id: 'alertDescriptionDelete',
    defaultMessage:
      'User {email} is the only assigned investigator on the investigations below. Assign another investigator first, then you can delete the user.',
    values: { email: true },
  },
  {
    id: 'alertDescriptionRevoke',
    defaultMessage:
      'User {email} is the only assigned investigator on the investigations below. Assign another investigator first, then you can change their permissions.',
    values: { email: true },
  },
  {
    id: 'alertDescriptionNoCandidates',
    defaultMessage:
      'No other member has investigation access to take over their cases below. Grant someone else access to the investigation module, then retry.',
  },
  { id: 'openMembersSettings', defaultMessage: 'Go to members settings' },
  { id: 'assignHeading', defaultMessage: 'Assign an investigator to each of these investigations' },
  { id: 'columnId', defaultMessage: 'ID' },
  { id: 'columnName', defaultMessage: 'Name' },
  { id: 'columnInvestigator', defaultMessage: 'Investigator' },
  { id: 'investigatorPlaceholder', defaultMessage: 'Select investigator' },
  { id: 'remove', defaultMessage: 'Remove' },
  { id: 'cancel', defaultMessage: 'Cancel' },
  { id: 'close', defaultMessage: 'Close' },
  { id: 'submitDelete', defaultMessage: 'Save and delete user' },
  { id: 'submitRevoke', defaultMessage: 'Save & update permissions' },
  {
    id: 'reassignFailed',
    defaultMessage: 'Couldn’t reassign the investigations. Please try again.',
  },
] as const)

type MemberDisplayProps = {
  member: { name: string | null; email: string | null; id: string }
}

const MemberDisplay = ({ member }: MemberDisplayProps) => {
  const primary = member.name ?? member.email ?? member.id
  // Only show the email as a second line when the name is what's on top.
  const secondary = member.name ? member.email : null
  return (
    <div className='flex flex-col'>
      <Typography.Text className='font-medium leading-[1.6]'>{primary}</Typography.Text>
      {secondary && (
        <Typography.Text size='small' className='text-colorTextTertiary font-normal leading-[1.6]'>
          {secondary}
        </Typography.Text>
      )}
    </div>
  )
}

type LastInvestigatorModalProps = {
  memberId: string
  memberEmail: string
  action: 'delete' | 'revoke'
  open: boolean
  onClose: () => void
  // Original action (delete / edit) — runs once every orphaned investigation has been reassigned.
  onContinue: () => Promise<void> | void
  // Navigate to the members-settings page from the dead-end (no-candidates) alert. Optional:
  // apps without a dedicated members route (kredenc) omit it and the action button doesn't render.
  onOpenMembersSettings?: () => void
}

const LastInvestigatorModalInner = ({
  memberId,
  memberEmail,
  action,
  open,
  onClose,
  onContinue,
  onOpenMembersSettings,
}: LastInvestigatorModalProps) => {
  const { formatMessage } = useIntl()
  const { getMotherId } = useMotherId()

  const { data: orphanedInvestigations } = useSuspenseQuery(
    trpc.investigation.listOrphanedForMember.queryOptions({
      memberId,
      institutionId: getMotherId(),
    })
  )
  const { data: investigatorMembers } = useSuspenseQuery(
    trpc.institution.listInvestigatorMembers.queryOptions({ institutionId: getMotherId() })
  )
  // Exclude the member being removed — they can't be their own replacement.
  const candidates = investigatorMembers.filter(member => member.id !== memberId)
  // When nobody else holds investigation access, reassignment is impossible: show a warning-only
  // dead-end state (no investigator picker, no submit) until someone is granted access.
  const hasCandidates = candidates.length > 0

  // Investigator chosen per investigation; consumed by the reassignment step below.
  const [selectedByInvestigation, setSelectedByInvestigation] = useState<
    Record<string, string | undefined>
  >({})

  const reassign = useMutation({
    ...trpc.investigation.investigator.reassign.mutationOptions(),
    // The error is handled inline (retry alert below), so suppress the global error toast.
    meta: { skipGlobalErrorHandler: true },
  })
  // Pending state for the destructive `onContinue()` that runs right after reassignment. It's an
  // arbitrary parent callback, so there's no mutation state to read it from.
  const [isContinuing, setIsContinuing] = useState(false)

  const allRowsSelected = orphanedInvestigations.every(
    investigation => selectedByInvestigation[investigation.id]
  )

  const onSubmit = () => {
    // One atomic call: the backend reassigns every investigation in a single transaction, so
    // there's no partial-success state to reconcile on retry.
    reassign.mutate(
      {
        reassignments: orphanedInvestigations.map(investigation => ({
          investigationId: investigation.id,
          // Button stays disabled until every row is selected, so this is always set.
          investigatorId: selectedByInvestigation[investigation.id] ?? '',
        })),
        // The member being removed is the outgoing last investigator; the server checks that
        // removing them would actually orphan these investigations before allowing the bypass.
        lastInvestigatorId: memberId,
      },
      {
        // Reassignment succeeded — run the original delete/revoke action in the same click.
        onSuccess: async () => {
          setIsContinuing(true)
          try {
            await onContinue()
          } finally {
            setIsContinuing(false)
          }
        },
      }
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={720}
      title={
        <FormattedMessage
          message={action === 'delete' ? messages.titleDelete : messages.titleRevoke}
        />
      }
      footer={
        <div className='flex justify-end gap-2'>
          <Button onClick={onClose} disabled={isContinuing || reassign.isPending}>
            <FormattedMessage message={hasCandidates ? messages.cancel : messages.close} />
          </Button>
          <Button
            type='primary'
            disabled={!hasCandidates || !allRowsSelected || reassign.isPending || isContinuing}
            loading={reassign.isPending || isContinuing}
            onClick={onSubmit}
          >
            <FormattedMessage
              message={action === 'delete' ? messages.submitDelete : messages.submitRevoke}
            />
          </Button>
        </div>
      }
    >
      <div className='flex flex-col gap-6'>
        <Alert
          className='[&_.ant-alert-action]:self-center'
          type={hasCandidates ? 'warning' : 'error'}
          message={formatMessage(
            action === 'delete' ? messages.alertTitleDelete : messages.alertTitleRevoke
          )}
          description={
            hasCandidates
              ? formatMessage(
                  action === 'delete'
                    ? messages.alertDescriptionDelete
                    : messages.alertDescriptionRevoke,
                  { values: { email: memberEmail } }
                )
              : formatMessage(messages.alertDescriptionNoCandidates)
          }
          action={
            !hasCandidates && onOpenMembersSettings ? (
              <Button onClick={onOpenMembersSettings}>
                <FormattedMessage message={messages.openMembersSettings} />
              </Button>
            ) : undefined
          }
        />
        {reassign.isError && (
          <Alert type='error' message={<FormattedMessage message={messages.reassignFailed} />} />
        )}
        <div className='flex flex-col gap-4'>
          {hasCandidates && (
            <Typography.Title level={5}>
              <FormattedMessage message={messages.assignHeading} />
            </Typography.Title>
          )}
          <Table
            dataSource={orphanedInvestigations}
            rowKey='id'
            pagination={false}
            columns={[
              {
                title: formatMessage(messages.columnId),
                fitWidthToContent: true,
                render: investigation => <Typography.Text>{investigation.tag}</Typography.Text>,
              },
              {
                title: formatMessage(messages.columnName),
                render: investigation => (
                  <Typography.Text strong>{investigation.name}</Typography.Text>
                ),
              },
              {
                title: formatMessage(messages.columnInvestigator),
                render: investigation => {
                  // Dead end: nobody can take over, so there's no picker — flag each row instead.
                  if (!hasCandidates) {
                    return (
                      <UntitledIcon
                        icon={ulAlertTriangle}
                        className='text-colorWarning'
                        size={24}
                      />
                    )
                  }

                  const selected = candidates.find(
                    candidate => candidate.id === selectedByInvestigation[investigation.id]
                  )

                  if (selected) {
                    return (
                      <div className='flex items-center justify-between gap-2'>
                        <MemberDisplay member={selected} />
                        <Button
                          type='text'
                          icon={<UntitledIcon icon={ulTrash01} />}
                          label={formatMessage(messages.remove)}
                          className='bg-colorFillTertiary hover:!bg-colorFillSecondary'
                          onClick={() =>
                            setSelectedByInvestigation(previous => ({
                              ...previous,
                              [investigation.id]: undefined,
                            }))
                          }
                        />
                      </div>
                    )
                  }

                  return (
                    <AutoComplete
                      className='w-full'
                      filterOption={(input, option) => {
                        const candidate = candidates.find(({ id }) => id === option?.value)
                        return `${candidate?.name ?? ''} ${candidate?.email ?? ''}`
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }}
                      onSelect={(_, option) =>
                        setSelectedByInvestigation(previous => ({
                          ...previous,
                          [investigation.id]: option.value,
                        }))
                      }
                      options={candidates.map(candidate => ({
                        value: candidate.id,
                        label: <MemberDisplay member={candidate} />,
                      }))}
                    >
                      <Input.Search
                        aria-label={formatMessage(messages.investigatorPlaceholder)}
                        className='[&_.ant-input-search-button]:px-0'
                        placeholder={formatMessage(messages.investigatorPlaceholder)}
                        enterButton={
                          <UntitledIcon className='text-white' icon={ulSearchMd} size={16} />
                        }
                      />
                    </AutoComplete>
                  )
                },
              },
            ]}
          />
        </div>
      </div>
    </Modal>
  )
}

export const LastInvestigatorModal = withModalSuspense(LastInvestigatorModalInner)
